import { useEffect, useState } from 'react';
import { neoApi, type NeoOnbTemplate } from '../api';
import { Page, Card, Button } from '../ui';

/**
 * Tipos de Onboarding (Admin) — os fluxos que aparecem em "Novo projeto". Cada tipo
 * tem ícone, nome, descrição, nº de perguntas obrigatórias e seu formulário. Editar
 * aqui vale na hora (guardado no hub).
 */

const inputStyle: React.CSSProperties = {
  padding: 'var(--space-2)',
  borderRadius: 'var(--radius-md, 8px)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  color: 'var(--color-fg)',
  font: 'inherit',
  width: '100%',
};

export function OnboardingTypes() {
  const [items, setItems] = useState<NeoOnbTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    neoApi
      .onboardingTemplates()
      .then((r) => setItems(r.templates))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const up = (i: number, patch: Partial<NeoOnbTemplate>) =>
    setItems((l) => l.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  const upQ = (ti: number, qi: number, patch: Partial<NeoOnbTemplate['questions'][number]>) =>
    setItems((l) => l.map((t, k) => (k === ti ? { ...t, questions: t.questions.map((q, j) => (j === qi ? { ...q, ...patch } : q)) } : t)));
  const addQ = (ti: number) =>
    setItems((l) => l.map((t, k) => (k === ti ? { ...t, questions: [...t.questions, { questionKey: `q${t.questions.length + 1}`, question: '', hint: '' }] } : t)));
  const rmQ = (ti: number, qi: number) =>
    setItems((l) => l.map((t, k) => (k === ti ? { ...t, questions: t.questions.filter((_, j) => j !== qi) } : t)));
  const addType = () =>
    setItems((l) => [...l, { id: '', icon: '📄', name: 'Novo tipo', desc: '', required: 1, questions: [{ questionKey: 'q1', question: '', hint: '' }] }]);
  const rmType = (ti: number) => setItems((l) => l.filter((_, i) => i !== ti));

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const r = await neoApi.saveOnboardingTemplates(items);
      setItems(r.templates);
      setMsg('Salvo — já vale no "Novo projeto".');
    } catch {
      setMsg('Não consegui salvar (confira nome e perguntas de cada tipo).');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page
      title="Tipos de Onboarding"
      subtitle="Os fluxos que aparecem em Novo Projeto. Cada tipo tem o seu formulário."
      actions={
        <Button variant="primary" onClick={save} disabled={saving || loading}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      }
    >
      {msg && <div style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>{msg}</div>}
      {loading ? (
        <div style={{ color: 'var(--color-fg-muted)' }}>Carregando…</div>
      ) : (
        <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {items.map((t, ti) => (
            <Card key={ti} size="normal">
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                <input value={t.icon} onChange={(e) => up(ti, { icon: e.target.value })} style={{ ...inputStyle, width: 56, textAlign: 'center', fontSize: 20 }} title="Ícone (emoji)" />
                <input value={t.name} onChange={(e) => up(ti, { name: e.target.value })} placeholder="Nome do tipo" style={{ ...inputStyle, fontWeight: 600 }} />
                <button onClick={() => rmType(ti)} title="Remover tipo" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-fg-muted)', fontSize: 18, padding: '0 6px' }}>✕</button>
              </div>
              <input value={t.desc} onChange={(e) => up(ti, { desc: e.target.value })} placeholder="Descrição curta (o que esse fluxo faz)" style={{ ...inputStyle, marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }} />

              <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-fg-muted)' }}>Perguntas</div>
              {t.questions.map((q, qi) => (
                <div key={qi} style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)', alignItems: 'center' }}>
                  <span style={{ color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)', width: 16 }}>{qi + 1}</span>
                  <input value={q.question} onChange={(e) => upQ(ti, qi, { question: e.target.value })} placeholder="Pergunta" style={{ ...inputStyle, flex: 2 }} />
                  <input value={q.hint ?? ''} onChange={(e) => upQ(ti, qi, { hint: e.target.value })} placeholder="Dica (opcional)" style={{ ...inputStyle, flex: 1, fontSize: 'var(--font-size-sm)' }} />
                  <button onClick={() => rmQ(ti, qi)} title="Remover pergunta" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-fg-muted)', padding: '0 4px' }}>✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-2)' }}>
                <Button variant="ghost" onClick={() => addQ(ti)}>+ pergunta</Button>
                <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Obrigatórias:
                  <input
                    type="number"
                    min={0}
                    max={t.questions.length}
                    value={t.required}
                    onChange={(e) => up(ti, { required: Math.max(0, Math.min(t.questions.length, Number(e.target.value) || 0)) })}
                    style={{ ...inputStyle, width: 60 }}
                  />
                </label>
              </div>
            </Card>
          ))}
          <div>
            <Button variant="ghost" onClick={addType}>+ Novo tipo</Button>
          </div>
        </div>
      )}
    </Page>
  );
}
